import { isEqual } from 'lodash';
import { useIntl } from 'react-intl';
import { targetingConstants, VariableSelector } from 'sonata-platform-components';
import { Checkbox, RadioButtonsGroup } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { normalizeIABVariables } from 'sonata-static-storage/lib/helpers/variables.helpers';
import { iabCategoriesDictionary } from 'sonata-static-storage/lib/samples/iabCategories.samples';
import { iabCategoriesV2Dictionary } from 'sonata-static-storage/lib/samples/iabCategoriesV2.samples';
import { useTheme } from 'styled-components';

import { FormExpandableSection } from '@/components/common/FormExpandableSection';
import { getVariableSelectorOptions } from '@/components/Targeting/helpers/helpers';
import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { getAppAndSitesCategoryTags } from './helpers/getSectionHeaderTags.helper';

import './appAndSitesCategories.styles.scss';

const { DEFAULT_APP_AND_SITES_CATEGORIES } = targetingConstants;

type Props = {
  levelToExcludeV1?: string;
  levelToExcludeV2?: string;
};

function AppAndSitesCategories({ levelToExcludeV1, levelToExcludeV2 }: Readonly<Props>) {
  const { setIabCatVersion, setProp, setUnknownIncluded, targeting, user, userRoles, resetSection } =
    useTargetingContext();

  const { categories, iabCatVersion, unknownIncluded } = targeting;

  const iabTargetingCategoriesV2Permission = checkUserResourcePermissions('iabTargetingCategoriesV2', user, userRoles);
  const unknownIncludedPermission = checkUserResourcePermissions('unknownIncluded', user, userRoles);

  const { uiColors }: any = useTheme();
  const { formatMessage } = useIntl();

  const version = iabCatVersion.categories;

  const { levels: iabCategoriesLevels } = normalizeIABVariables(iabCategoriesDictionary);
  const { levels: iabCategoriesLevelsV2 } = normalizeIABVariables(iabCategoriesV2Dictionary);

  const levelToExclude = version === 1 ? levelToExcludeV1 : levelToExcludeV2;
  const iabLevels = version === 1 ? iabCategoriesLevels : iabCategoriesLevelsV2;
  const options = getVariableSelectorOptions(iabLevels, formatMessage, levelToExclude);

  const radioButtonGroupItems = [
    {
      id: 'IABv1',
      text: formatMessage({
        defaultMessage: 'IAB v.1',
        id: 'targeting.appAndSitesCategories.radioButtonsList.item1'
      })
    },
    {
      id: 'IABv2',
      isDisabled: !iabTargetingCategoriesV2Permission,
      text: formatMessage({
        defaultMessage: 'IAB v.2',
        id: 'targeting.appAndSitesCategories.radioButtonsList.item2'
      })
    }
  ];

  const handleVariablesChange = (values: string[]) => {
    if (!isEqual(values, categories)) {
      setProp('categories', values);
    }
  };

  const handleCategoryChange = (id: string) => {
    const versionVal = id === 'IABv1' ? 1 : 2;
    if (versionVal !== version) {
      setIabCatVersion('categories', versionVal);
    }
  };

  const handleResetAppAndSitesCategories = () => {
    resetSection('appAndSitesCategories');
  };

  const selectedRadioButtonItemId = radioButtonGroupItems.find(rb => rb.id === `IABv${version}`)?.id;
  const isSectionOpened = !isEqual(categories, DEFAULT_APP_AND_SITES_CATEGORIES.categories);
  const appAndSitesCategoryTags = getAppAndSitesCategoryTags(version, categories);

  return (
    <div className="appAndSitesCategories" id="appAndSitesCategoriesBox">
      <FormExpandableSection
        defaultOpen={isSectionOpened}
        onReset={handleResetAppAndSitesCategories}
        preffixId="appAndSitesCategories"
        tags={appAndSitesCategoryTags}
        title={formatMessage({
          defaultMessage: 'App & Sites Categories',
          id: 'targeting.appAndSitesCategories.title'
        })}
        tooltipText={formatMessage({
          defaultMessage:
            'Choose the overarching themes or topics of the apps and websites where your ads will be displayed.',
          id: 'targeting.appAndSitesCategories.tooltip'
        })}
      >
        <div className="appAndSitesCategories__boxBody">
          <div className="appAndSitesCategories__topRow">
            <div className="appAndSitesCategories__radioButtonListContainer">
              <RadioButtonsGroup
                id={'appAndSitesCategoriesRadioButtonList'}
                items={radioButtonGroupItems}
                onChange={handleCategoryChange}
                selectedItem={selectedRadioButtonItemId}
              />
            </div>
            {unknownIncludedPermission && categories.length > 0 && (
              <Checkbox
                classes="appAndSitesCategories__checkbox"
                defaultActive={unknownIncluded.categories}
                id={'categories'}
                labelColor={uiColors.textBodyDefault}
                labelText={formatMessage({
                  defaultMessage: 'Unknown Included',
                  id: 'targeting.appAndSitesCategories.checkbox.label'
                })}
                onClickAction={(id: string, value: boolean) => setUnknownIncluded(id, value)}
              />
            )}
          </div>
          <div className="appAndSitesCategories__variablesContainer">
            <VariableSelector
              idsSelectedItems={categories}
              items={iabLevels}
              levelToExclude={levelToExclude}
              onChange={(values: string[]) => handleVariablesChange(values)}
              options={options}
            />
          </div>
        </div>
      </FormExpandableSection>
    </div>
  );
}

export default AppAndSitesCategories;
